import { useEffect, useState } from 'react';
import { CircularProgress, Container, Paper, Typography } from '@material-ui/core';

import authService from '#root/services/auth';

interface AuthDebugResponse {
  environment: {
    THIS_ENV: string;
    PHP_VERSION: string;
    SERVER_TIME: string;
  };
  session: {
    session_id: string;
    session_name: string;
    session_status: number;
  };
  auth: {
    logged: boolean;
    idu: number;
    user_email: string | null;
    is_admin: boolean;
    is_superadmin: boolean;
    is_viajero: boolean;
  };
  ip_detection: {
    client_ip: string;
    REMOTE_ADDR: string;
    HTTP_X_FORWARDED_FOR: string;
    HTTP_CLIENT_IP: string;
  };
  tokens: {
    today_token_tip: string;
    today_token_tip_formula: string;
    gat_encoded: string;
    gat_decoded_test: string;
  };
  cookies_received: {
    GuverySession: string;
    GuveryTIP: string;
    GuveryAuth: string;
  };
  cookie_config: {
    MAIN_SESSION_COOKIE_NAME: string;
    MAIN_SESSION_COOKIE_DOMAIN: string;
  };
  cors_config: {
    IP_APP_REACT: string;
  };
  validation_test: {
    tip_cookie_matches_generated: string;
    gat_cookie_matches_generated: string;
  };
}

const AuthDebug = () => {
  const [data, setData] = useState<AuthDebugResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDebug = async () => {
      try {
        const response = await authService.debug();
        if (response.error) {
          setError(response.message || 'Error al obtener debug');
        } else {
          setData(response.data);
        }
      } catch (err) {
        setError('Error de conexión');
      } finally {
        setLoading(false);
      }
    };
    fetchDebug();
  }, []);

  const renderValidationBadge = (value: string) => {
    const isYes = value === 'YES';
    return (
      <span
        style={{
          color: isYes ? '#2e7d32' : '#c62828',
          fontWeight: 'bold',
          backgroundColor: isYes ? '#e8f5e9' : '#ffebee',
          padding: '2px 8px',
          borderRadius: '4px',
        }}>
        {value}
      </span>
    );
  };

  const renderSection = (title: string, content: Record<string, any>) => (
    <Paper style={{ padding: 16, marginBottom: 16 }}>
      <Typography variant="h6" style={{ marginBottom: 8, color: '#1976d2' }}>
        {title}
      </Typography>
      <pre style={{ margin: 0, fontSize: 13, overflow: 'auto' }}>
        {Object.entries(content).map(([key, value]) => {
          if (title === 'validation_test') {
            return (
              <div key={key} style={{ marginBottom: 4 }}>
                <strong>{key}:</strong> {renderValidationBadge(String(value))}
              </div>
            );
          }
          return (
            <div key={key} style={{ marginBottom: 4 }}>
              <strong>{key}:</strong> {JSON.stringify(value)}
            </div>
          );
        })}
      </pre>
    </Paper>
  );

  if (loading) {
    return (
      <Container style={{ textAlign: 'center', paddingTop: 100 }}>
        <CircularProgress />
        <Typography style={{ marginTop: 16 }}>Cargando debug de autenticación...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ textAlign: 'center', paddingTop: 100 }}>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container style={{ textAlign: 'center', paddingTop: 100 }}>
        <Typography>No hay datos disponibles</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" style={{ paddingTop: 32, paddingBottom: 32 }}>
      <Typography variant="h4" style={{ marginBottom: 24 }}>
        🔐 Auth Debug
      </Typography>

      {renderSection('environment', data.environment)}
      {renderSection('session', data.session)}
      {renderSection('auth', data.auth)}
      {renderSection('ip_detection', data.ip_detection)}
      {renderSection('tokens', data.tokens)}
      {renderSection('cookies_received', data.cookies_received)}
      {renderSection('cookie_config', data.cookie_config)}
      {renderSection('cors_config', data.cors_config)}
      {renderSection('validation_test', data.validation_test)}

      <Paper style={{ padding: 16, backgroundColor: '#f5f5f5' }}>
        <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
          Raw JSON
        </Typography>
        <pre style={{ margin: 0, fontSize: 12, overflow: 'auto', maxHeight: 300 }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      </Paper>
    </Container>
  );
};

export default AuthDebug;
